import React from 'react';
import PropTypes from 'prop-types';
import autobind from 'autobind-decorator';

import {withI18n} from 'spa/Components/Context';

import FormGroup from './FormGroup';

import PhoneInput, {isPossiblePhoneNumber} from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en';
import fr from 'react-phone-number-input/locale/fr';

import {cn} from 'spa/css';

import './InputField.less';
import '../phone-number-input.less';

import errorIcon from 'spa/images/icons/error-new.svg';

@withI18n
class PhoneNumberInputField extends React.Component {
  static propTypes = {
    children: PropTypes.any,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.string,
    groupClassName: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    readOnly: PropTypes.bool,
    required: PropTypes.bool,
    skipInitialCheck: PropTypes.bool,
    squareTheme: PropTypes.bool,
    validateOnChange: PropTypes.bool,
    whiteBackground: PropTypes.bool,
  };

  static defaultProps = {};

  static contextTypes = {
    formHasSubmitted: PropTypes.bool,
  };

  state = {
    touched: false, errors: [], isValid: true, value: undefined,
  };

  componentDidMount() {
    const {skipInitialCheck} = this.props;

    if (!skipInitialCheck) {
      this.checkValidity();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.error !== this.props.error) {
      const {input} = this;
      if (input && input.current && input.current.setCustomValidity) {
        input.current.setCustomValidity(nextProps.error || '');
        this.checkValidity();
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.required !== this.props.required
    ) {
      this.checkValidity();
    }
  }

  input = React.createRef();

  get validity() {
    return this.input && this.input.current && this.input.current.validity;
  }

  @autobind onChange(value) {
    if (this.props.onChange) this.props.onChange(value);
    this.setState({value});
    if (this.props.validateOnChange) {
      this.setState({touched: true});
      this.checkValidity();
    }
  }

  @autobind onBlur(event) {
    if (this.input && this.input.current) {
      this.input.current.setCustomValidity('');
    }

    if (this.props.onBlur) this.props.onBlur(event);

    this.setState({touched: true});
    this.checkValidity();
  }

  checkValidity() {
    const errors = [];
    const {input} = this;

    if (!input || !input.current) {
      this.setState({errors});
      return;
    }

    const {i18n} = this.context;
    const {validity, value} = input.current;
    let isValid = true;
    if (validity) {
      isValid = validity.valid;
      if (validity.valueMissing) {
        errors.push({
          key: 'valueMissing',
          message: i18n.__('%(label)s is required', this.props),
        });
      }
      if (validity.tooShort) {
        errors.push({
          key: 'tooShort',
          message: i18n.__('%(label)s is too short', this.props),
        });
      }
      if (validity.tooLong) {
        errors.push({
          key: 'tooLong',
          message: i18n.__('%(label)s is too long', this.props),
        });
      }
      if (validity.patternMismatch) {
        errors.push({
          key: 'patternMismatch',
          message: i18n.__('%(label)s is invalid', this.props),
        });
      }
      if (validity.typeMismatch && !validity.patternMismatch) {
        errors.push({
          key: 'typeMismatch',
          message: i18n.__('%(label)s must be valid', this.props),
        });
      }
      if (value && !isPossiblePhoneNumber(value)) {
        isValid = false;
        input.current.setCustomValidity(i18n.__('%(label)s is invalid', this.props));
        errors.push({
          key: 'invalidPhoneNumber',
          message: i18n.__('%(label)s is invalid', this.props),
        });
      }
    }

    this.setState({errors, isValid});
  }

  render() {
    const {
      children,
      className,
      disabled,
      error,
      groupClassName,
      id,
      label,
      name,
      placeholder,
      readOnly,
      squareTheme,
      whiteBackground,
      ...rest
    } = this.props;
    const {
      errors, isValid, touched, value,
    } = this.state;
    const {formHasSubmitted, i18n} = this.context;

    const isInvalid = (touched || formHasSubmitted) && (!isValid || error);

    return (
      <div>
        <FormGroup
          label={label}
          htmlFor={id}
          className={cn(
            'form-group--input form-group--new-layout',
            isInvalid && 'form-group--invalid',
            disabled && 'form-group--disabled',
            readOnly && 'form-group--read-only',
            squareTheme && 'form-group--square',
            !whiteBackground && 'form-group--dark-layout',
            groupClassName,
          )}>
          {isInvalid &&
            <img className="form-field__error-icon" src={errorIcon} alt={i18n._x('Signup error icon', 'Form error icon')} />
          }
          <div className="form-field">
            <PhonNumberInputFieldElem
              key="input"
              id={id}
              className={cn('form-control', className, isInvalid && 'form-control--invalid')}
              data-qa={`${name}-input`}
              name={name}
              disabled={disabled}
              region={i18n.region}
              language={i18n.safeLanguage}
              ref={this.input}
              placeholder={placeholder}
              value={value}
              onBlur={this.onBlur}
              onChange={this.onChange}
              // onCountryChange={this.onChangeCountry} not needed right now but may be necessary for option we go with
              required
              {...rest}
            />
          </div>
          {children}
        </FormGroup>
        {isInvalid && (
          [{key: 'custom', message: error}, ...errors]
            .map(({key, message}) =>
              (message ? <p key={key} className="form-field-error form-field-error--new-layout" data-qa={`${name}-input-error`}>{message}</p> : null))
        )}
      </div>
    );
  }
}

export default PhoneNumberInputField;

const PhonNumberInputFieldElem = React.forwardRef((props, ref) => (
  <PhoneInput
    ref={ref}
    international
    addInternationalOption={false}
    countryOptionsOrder={props.region === 'GB' ? ['GB'] : ['FR']}
    defaultCountry={props.region}
    labels={props.language === 'en' ? en : fr}
    {...props}
  />
));

PhonNumberInputFieldElem.propTypes = {
  language: PropTypes.oneOf(['en', 'fr']),
  region: PropTypes.oneOf(['GB', 'FR']),
};

PhonNumberInputFieldElem.defaultProps = {
  language: 'en',
  region: 'GB',
};




// WEBPACK FOOTER //
// ./spa/Components/Form/PhoneNumberInputField.jsx